import { MEAL_PLAN_REGIONS_IDS } from 'common/Constants';
import { ClientEvents } from 'common/events/ClientEvents';
import { QueryParams } from 'common/urls/QueryParams';
import { get } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router';
import { BrowseFilter } from 'src/gqlReactTypings.generated.d';
import { MealPlanSubscriptionContext } from 'src/pages/consumer/meal-plans/MealPlanSubscriptionsContext';
import { useFeatureFlag } from 'src/shared/design-system/FeatureFlag';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { Tabs } from 'web-common/src/shared/design-system/components/Tabs';
import { DishFirstExplore } from '../DishFirstExplore';
import { ExploreTabNames } from './consts';
import { WeeklyShefServicePage } from './WeeklyShefServicePage';
import { WeeklyShefServiceBanner } from './WeeklyShefServicePage/WeeklyShefServiceBanner';

interface ExplorePageProps extends RouteComponentProps {}

export const ExplorePage: React.FC<ExplorePageProps> = (props) => {
  const [, , zipCodeMetaData] = useCachedZipCode();
  const tracker = useTracker();
  const regionId = get(zipCodeMetaData, 'cachedZipCodeData.zipCode.region.id', null);
  const isMealPlanRegion = MEAL_PLAN_REGIONS_IDS.includes(regionId);
  const exploreWeeklyShefServiceTabsEnabled = useFeatureFlag('exploreWeeklyShefServiceTabsEnabled');
  const { userHasActiveMealPlan, mealPlanDataLoaded } = useContext(MealPlanSubscriptionContext);

  const [{ tab, bf, cuisine, sa }, setParams] = useQueryParams({
    [QueryParams.TAB]: withDefault(StringParam, undefined),
    [QueryParams.BROWSE_FILTER]: withDefault(StringParam, undefined),
    [QueryParams.CUISINE_FILTER]: withDefault(StringParam, undefined),
    [QueryParams.SEE_ALL]: withDefault(StringParam, undefined),
  });

  const isOnCuisinePage = bf === BrowseFilter.Cuisine && !!cuisine && sa;
  const changeTab = useCallback(
    (tabName: string) => {
      tracker.track(ClientEvents.WSS_EXPLORE_TAB_CLICK, {
        userHasActiveMealPlan,
        tab: tabName === ExploreTabNames.ShefService ? 'weekly-shef-service' : 'one-time-ordering',
      });
      setParams({ tab: tabName }, 'replaceIn');
    },
    [setParams]
  );

  const defaultTab = useMemo(() => {
    if (tab) return tab;
    if (!mealPlanDataLoaded) return '';
    if (userHasActiveMealPlan) return ExploreTabNames.ShefService;
    return ExploreTabNames.OneTime;
  }, [userHasActiveMealPlan, mealPlanDataLoaded]); // don't add tab here, it will cause this to retrigger

  useEffect(() => {
    if (!tab && defaultTab) {
      setParams({ tab: defaultTab }, 'replaceIn');
    }
  }, [defaultTab, tab, setParams]);

  const dishFirstExplore = <DishFirstExplore {...props} />;

  if (!exploreWeeklyShefServiceTabsEnabled || !isMealPlanRegion || isOnCuisinePage) {
    return dishFirstExplore;
  }

  const activeTabName = tab ?? defaultTab;

  const tabs = [
    {
      label: 'Weekly Shef Service',
      tabName: ExploreTabNames.ShefService,
      component: <WeeklyShefServicePage />,
    },
    {
      label: 'One-time ordering',
      tabName: ExploreTabNames.OneTime,
      component: dishFirstExplore,
    },
  ];

  return (
    <>
      {!userHasActiveMealPlan && mealPlanDataLoaded && <WeeklyShefServiceBanner />}
      <Tabs tabs={tabs} activeTabName={activeTabName} onTabClick={changeTab} />
    </>
  );
};

export default ExplorePage;
